"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Share2, Download, Copy, X, Check } from "lucide-react"
import { ShareCard } from "./share-card"
import { buildShareText } from "@/lib/share-text"

interface ShareButtonProps {
  type: "standard" | "daily" | "timed" | "versus"
  score: number
  gameType: string
  extra?: {
    difficulty?: string
    date?: string
    dateId?: string
    rank?: string
  }
}

export function ShareButton({ type, score, gameType, extra }: ShareButtonProps) {
  const [showModal, setShowModal] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    if (showModal) dialogRef.current?.showModal()
  }, [showModal])
  const cardRef = useRef<HTMLDivElement>(null)

  const getShareText = () => {
    if (type === "daily") {
      return buildShareText({
        type,
        score,
        gameType,
        date: extra?.dateId ?? new Date().toISOString().slice(0, 10),
      })
    }

    return buildShareText({
      type,
      score,
      gameType,
      difficulty: extra?.difficulty,
    })
  }

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return null
    setGenerating(true)
    try {
      const html2canvas = (await import("html2canvas-pro")).default
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
      })
      const url = canvas.toDataURL("image/png")
      setImageUrl(url)
      setGenerating(false)
      return { canvas, url }
    } catch {
      setGenerating(false)
      return null
    }
  }, [])

  const handleOpen = async () => {
    setCopyError(false)
    setShowModal(true)
    // Wait for next frame so the card is rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        generateImage()
      })
    })
  }

  const handleDownload = async () => {
    const result = imageUrl ? { url: imageUrl } : await generateImage()
    if (!result?.url) return

    const link = document.createElement("a")
    link.download = `higherlower-${type}-score-${score}.png`
    link.href = result.url
    link.click()
  }

  const handleShare = async () => {
    const result = imageUrl ? { url: imageUrl } : await generateImage()
    if (!result?.url) { await handleCopyText(); return }

    try {
      const blob = await (await fetch(result.url)).blob()
      const file = new File([blob], `higherlower-${type}-score-${score}.png`, { type: "image/png" })

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          text: getShareText(),
          files: [file],
        })
      } else {
        await handleCopyText()
      }
    } catch {
      // User cancelled or error
    }
  }

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(getShareText())
      setCopyError(false)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopyError(true)
    }
  }

  return (
    <>
      <Button
        onClick={handleOpen}
        className="h-14 text-lg rounded-xl gap-2 border border-[#171717]/15 bg-[#eee6db] text-[#171717] hover:bg-[#e5dccf]"
      >
        <Share2 className="h-5 w-5" />
        Share
      </Button>

      {showModal && (
        <dialog
          ref={dialogRef}
          onClose={() => { setShowModal(false); setImageUrl(null) }}
          className="m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-y-auto overscroll-contain rounded-[24px] border border-[#171717]/15 bg-[#fbf7ef] p-0 shadow-[0_28px_70px_rgb(0_0_0/0.24)] backdrop:bg-black/60 backdrop:backdrop-blur-sm"
          aria-labelledby="share-score-title"
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#d8d0c4]">
              <h3 id="share-score-title" className="text-lg font-bold text-[#171717]">Share Your Score</h3>
              <button
                onClick={() => dialogRef.current?.close()}
                className="p-2 rounded-lg text-[#655d54] hover:bg-[#ff6f61]/15 hover:text-[#171717]"
                aria-label="Close share dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Card preview */}
            <div className="p-4">
              {/* Hidden card for rendering */}
              <div aria-hidden="true" style={{ position: "fixed", left: -9999, top: -9999 }}>
                <ShareCard
                  ref={cardRef}
                  type={type}
                  score={score}
                  gameType={gameType}
                  extra={extra}
                />
              </div>

              {/* Image preview */}
              <div className="rounded-xl overflow-hidden border border-[#d8d0c4] bg-[#eee6db] w-full aspect-[3/4] max-h-[50dvh] flex items-center justify-center">
                {imageUrl ? (
                  // This preview is generated locally from a canvas, so it needs no image optimizer.
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={imageUrl} alt="Share card preview" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-[#655d54] text-sm">
                    {generating ? "Generating..." : "Preparing preview..."}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 pt-0 flex gap-2">
              <Button
                onClick={handleShare}
                disabled={generating}
                className="coral-cta flex-1 h-12 rounded-xl gap-2 border bg-[#ff6f61] text-[#171717]"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
              <Button
                aria-label="Download score image"
                onClick={handleDownload}
                disabled={generating}
                className="h-12 rounded-xl gap-2 border border-[#171717]/15 bg-[#eee6db] text-[#171717] hover:bg-[#e5dccf]"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                aria-label={copied ? "Score copied" : "Copy score text"}
                onClick={handleCopyText}
                className="h-12 rounded-xl gap-2 border border-[#171717]/15 bg-[#eee6db] text-[#171717] hover:bg-[#e5dccf]"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          {copyError && <p role="status" className="px-4 pb-4 text-sm text-[#655d54]">Copying is unavailable. You can download the score image instead.</p>}
        </dialog>
      )}
    </>
  )
}
